import React, { useEffect, useRef, useState } from "react";
import { ThumbsDownBlackIcon } from "../../assets/ThumbsDownBlackIcon";
import { ThumbsDownRedIcon } from "../../assets/ThumbsDownRedIcon";
import { ThumbsUpBlackIcon } from "../../assets/ThumbsUpBlackIcon";
import { ThumbsUpGreenIcon } from "../../assets/ThumbsUpGreenIcon";
import { sendUserFeedback } from "../../utils/analyticsEvents";
import CustomChecklistItem from "../atoms/CustomChecklistItem";
import RoundedButton from "../atoms/RoundedButton";
import { useSearch } from "../../context/SearchContext";
interface UserFeedbackProps {
  requestId: string;
  contextId: string;
  query: string;
  isOpen: boolean;
  onClose: () => void;
  selectedFeedback: "negative" | "positive" | null;
  setSelectedFeedback: React.Dispatch<
    React.SetStateAction<"negative" | "positive" | null>
  >;
  onFeedbackChange: (feedback: "negative" | "positive" | null) => void;
  setIsFeedbackSent: React.Dispatch<React.SetStateAction<boolean>>;
  isFeedbackSent: boolean;
}

const UserFeedback: React.FC<UserFeedbackProps> = ({
  requestId,
  contextId,
  query,
  isOpen,
  onClose,
  selectedFeedback,
  setSelectedFeedback,
  onFeedbackChange,
  setIsFeedbackSent,
  isFeedbackSent,
}) => {
  const { textResponse, sources, duration, products, answerComponents } =
    useSearch();
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);

  const closePanel = () => {
    setSelectedFeedback(null);
    onClose();
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        closePanel();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onClose]);

  const handleFeedbackClick = (feedback: "negative" | "positive") => {
    onFeedbackChange(feedback);
    setSelectedOptions([]);
    setIsFeedbackSent(false);
  };

  const handleRecommendationToggle = (label: string) => {
    setSelectedOptions((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label],
    );
  };

  const isSubmitDisabled =
    !selectedFeedback ||
    (selectedOptions.length === 0 && feedbackText.trim() === "");

  const recommendationLabels = {
    negative: [
      "Información incorrecta",
      "No era lo que buscaba",
      "Es difícil de entender",
      "Falta ser más específico",
    ],
    neutral: [
      "Información incorrecta",
      "No era lo que buscaba",
      "Es difícil de entender",
      "Falta ser más específico",
    ],
    positive: [
      "Información precisa y completa",
      "Fácil de entender",
      "Útil y aplicable",
      "Respuesta rápida",
    ],
  };

  const handleSubmitFeedback = async () => {
    if (selectedFeedback === null) {
      return;
    }

    sendUserFeedback(
      requestId,
      query,
      selectedFeedback,
      selectedOptions,
      feedbackText,
      contextId,
      textResponse,
      sources,
      duration,
      products,
      answerComponents,
    );
    setIsFeedbackSent(true);
    closePanel();
    setSelectedOptions([]);
    setFeedbackText("");
  };

  return (
    <>
      <div
        className={`fixed inset-0 transition-opacity duration-300 ${
          isOpen ? "z-40" : "-z-10 pointer-events-none"
        } ${isOpen && isSmallScreen ? "bg-black bg-opacity-50" : ""}`}
        onClick={closePanel}
      ></div>

      <div
        className={`fixed bg-white transition-all duration-300 ease-in-out z-50 overflow-hidden shadow-[4px_4px_16px_rgba(0,0,0,0.2)]
          ${
            isSmallScreen
              ? "inset-x-0 bottom-0 w-full rounded-t-xl border-t border-gray-300"
              : "w-[350px] rounded-xl border-r border-b border-gray-300"
          }
          ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}
        `}
        style={{
          visibility: isOpen ? "visible" : "hidden",
          top: isSmallScreen ? "auto" : "auto",
          right: isSmallScreen ? "0" : "20px",
          transform: isSmallScreen ? "none" : "none",
          bottom: isSmallScreen ? "0" : "20px",
          left: isSmallScreen ? "0" : "auto",
        }}
        ref={panelRef}
      >
        <div
          className={`p-5 ${
            isSmallScreen ? "h-auto" : "h-full overflow-y-auto"
          } text-center`}
        >
          <button
            onClick={closePanel}
            className="absolute top-5 right-5 text-gray-500"
            aria-label="Cerrar"
          >
            <i className="pi pi-times font-semibold text-xl text-BCP-orange" />
          </button>

          <div className="pt-3 pb-2">
            <h2 className="font-flexo-demi text-lg text-BCP-dark-blue">
              ¿Resolvimos tu consulta?
            </h2>
            <div className="flex justify-center gap-4 my-5">
              <button
                className={`rounded-full py-2 px-4 flex items-center font-medium ${
                  selectedFeedback === "positive"
                    ? "bg-BCP-light-green font-flexo-demi"
                    : "bg-[#F6F6F7] hover:bg-BCP-light-green font-flexo-demi"
                }`}
                onClick={() => handleFeedbackClick("positive")}
              >
                {selectedFeedback === "positive" ? (
                  <ThumbsUpGreenIcon className="w-6 h-6 mr-2" />
                ) : (
                  <ThumbsUpBlackIcon className="w-6 h-6 mr-2" />
                )}
                <span
                  className={
                    selectedFeedback === "positive"
                      ? "text-[#37A500] font-flexo-demi"
                      : "text-[#264B78] font-flexo-demi"
                  }
                >
                  Sí
                </span>
              </button>
              <button
                className={`rounded-full py-2 px-4 flex items-center font-medium ${
                  selectedFeedback === "negative"
                    ? "bg-[#FFF2F5] font-flexo-demi"
                    : "bg-[#F6F6F7] hover:bg-[#FFF2F5] font-flexo-demi"
                }`}
                onClick={() => handleFeedbackClick("negative")}
              >
                {selectedFeedback === "negative" ? (
                  <ThumbsDownRedIcon className="w-6 h-6 mr-2" />
                ) : (
                  <ThumbsDownBlackIcon className="w-6 h-6 mr-2" />
                )}
                <span
                  className={
                    selectedFeedback === "negative"
                      ? "text-BCP-error font-flexo-demi"
                      : "text-[#264B78] font-flexo-demi"
                  }
                >
                  No
                </span>
              </button>
            </div>
          </div>

          {selectedFeedback && !isFeedbackSent && (
            <div>
              <h3 className="font-semibold mb-4 text-BCP-dark-blue text-center">
                {selectedFeedback === "positive"
                  ? "¡Excelente! ¿Qué fue lo que más te gustó?"
                  : "Tu opinión nos importa. ¿Qué no te gustó?"}
              </h3>
              <div className="flex flex-wrap justify-center gap-2 mb-5">
                {recommendationLabels[selectedFeedback].map((label) => (
                  <CustomChecklistItem
                    key={label}
                    label={label}
                    isSelected={selectedOptions.includes(label)}
                    onClick={() => handleRecommendationToggle(label)}
                    className="font-flexo-demi"
                  />
                ))}
              </div>
              <div className="mb-5">
                <textarea
                  placeholder="¿Tienes alguna sugerencia para mejorar?"
                  value={feedbackText}
                  onChange={(e) =>
                    setFeedbackText(e.target.value.slice(0, 500))
                  }
                  maxLength={500}
                  className="w-full h-[80px] mb-1 border text-BCP-dark-gray border-[#ddd] rounded-lg p-3 resize-none focus:outline-none focus:ring-1 focus:ring-BCP-blue font-flexo-demi"
                />
                <div className="text-right text-xs text-gray-500">
                  {feedbackText.length}/500
                </div>
              </div>
              <div
                className={`flex justify-center pb-3 ${
                  isSmallScreen ? "mt-auto" : ""
                }`}
              >
                <RoundedButton
                  label="Enviar"
                  onClick={handleSubmitFeedback}
                  disabled={isSubmitDisabled}
                  customClass={`h-[40px] ${
                    isSmallScreen ? "w-full" : "w-[180px]"
                  }`}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UserFeedback;
